import { HERO_MAPBOX_STYLE, MAPBOX_TOKEN } from "../config/mapbox";
import { TokenMeta } from "../types/LandToken";
import { landFocusZoom } from "./landMeta";

// Mapbox Static Images API helpers for decorative map backgrounds (the homepage hero).
//
// Style: the hero's own Studio style (HERO_MAPBOX_STYLE, since 2026-10-13), not the interactive
// maps' MAPBOX_STYLE: imagery and natural-feature labels only, no roads or place names.
// It must stay a CLASSIC style. The Static Images API renders a blank basemap for styles built
// on Mapbox Standard — our earlier Standard-based Studio style and mapbox/standard-satellite both
// came back HTTP 200 with nothing but the overlays and logo drawn (checked 2026-10-09; the docs
// say Standard and styles importing it are "not supported"). The current one is a classic
// Satellite Streets copy (checked 2026-10-13), so it renders. If HERO_MAPBOX_STYLE is ever moved
// to a Standard-based style, point this at mapbox/satellite-v9 instead.
//
// Referrer: the token is URL-restricted, so the request must carry the page's Referer. A plain
// <img src> does; never add referrerPolicy="no-referrer" (that's a 403).
//
// Attribution + logo (Mapbox Product Terms §1.4, checked 2026-10-12): the logo is required on every
// Mapbox map, custom Studio styles included; this style's sources (mapbox.satellite +
// mapbox-streets-v8) also require the STATIC_MAP_ATTRIBUTION links, Maxar for the imagery. Both
// are off in the image itself (`attribution=false&logo=false`) only because they sit in the very
// corner, where a rounded container clips them — the docs allow attribution "elsewhere on the
// webpage" — so whatever shows a static map must draw both near it:
// components/HomeHero/MapAttribution does (mapbox-gl's own logo and compact attribution control,
// drawn by mapbox-gl.css).
// Size requests to the box's aspect ratio anyway, so `cover` doesn't crop the map.

/** `owner/id` — the Static Images API path form of the `mapbox://styles/owner/id` URL. */
export const STATIC_MAP_STYLE = HERO_MAPBOX_STYLE.replace("mapbox://styles/", "");
/**
 * Zoom for a land with no usable size (see staticMapZoom): ~0.42 m per pixel around Sri Lanka's
 * latitude, so the scale bar reads 50 m ≈ 120px.
 */
export const STATIC_MAP_ZOOM = 17.5;
/**
 * Share of the map's shorter side a land should span. At the hero's sizes this lands on Explore's
 * own focus targets (LAND_TARGET_PX): ~240px of the 520px-tall desktop panel, ~160px of a phone's
 * square one.
 */
const LAND_SPAN = 0.55;
/** The API's per-side limit; anything larger is a 422. */
const MAX_SIDE = 1280;

/** Credits for STATIC_MAP_STYLE's imagery + streets (its tilesets' TileJSON), to show as links whenever a static map is on screen. */
export const STATIC_MAP_ATTRIBUTION = [
  { label: "© Mapbox", href: "https://www.mapbox.com/about/maps/" },
  { label: "© OpenStreetMap", href: "https://www.openstreetmap.org/copyright" },
  { label: "© Maxar", href: "https://www.maxar.com/" },
  // `improve`: mapbox-gl's `mapbox-improve-map` link (drawn bold by mapbox-gl.css).
  { label: "Improve this map", href: "https://www.mapbox.com/map-feedback/", improve: true },
];

export type LngLat = { lon: number; lat: number };

// Same tolerance as utils/landMeta's private parseLocation/num (kept in step with it):
// meta.location can arrive as an unparsed JSON string, and a bad payload must drop the
// coordinates rather than throw.
const parseLocation = (raw: unknown): Partial<TokenMeta["location"]> | null => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};
const num = (v: unknown) => (v === null || v === undefined || v === "" ? NaN : Number(v));

/** A land's coordinates from its meta, or null when missing or out of range. */
export const landLngLat = (meta?: Pick<TokenMeta, "location"> | null): LngLat | null => {
  const location = parseLocation(meta?.location);
  const lon = num(location?.lon);
  const lat = num(location?.lat);
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || Math.abs(lon) > 180 || Math.abs(lat) > 85) {
    return null;
  }
  return { lon, lat };
};

/**
 * The zoom that frames a land on a static map of this size (image px), the way Explore focuses
 * one: landFocusZoom sizes it to the land's area, clamped to [12, 18.42], and falls back to
 * STATIC_MAP_ZOOM when the land has no size. Sized in image px, so a map the API's limit made
 * smaller still shows the land at the same share once `cover` scales it up. Rounded to the API's
 * two decimals, so the scale bar measures exactly what's drawn.
 */
export const staticMapZoom = (meta: TokenMeta | null | undefined, lat: number, width: number, height: number) => {
  const zoom = landFocusZoom(meta, lat, LAND_SPAN * Math.min(width, height)) ?? STATIC_MAP_ZOOM;
  return Math.round(zoom * 100) / 100;
};

/** Fit a box into the API's 1280px limit, keeping its aspect ratio. */
export const staticMapSize = (width: number, height: number): { width: number; height: number } => {
  const k = Math.min(1, MAX_SIDE / Math.max(width, height, 1));
  return {
    width: Math.max(1, Math.round(width * k)),
    height: Math.max(1, Math.round(height * k)),
  };
};

export type StaticMapOptions = LngLat & {
  width: number;
  height: number;
  zoom?: number;
  style?: string;
  /** Request the @2x rendition (high-DPR screens). */
  retina?: boolean;
};

export const staticMapUrl = ({
  lon,
  lat,
  width,
  height,
  zoom = STATIC_MAP_ZOOM,
  style = STATIC_MAP_STYLE,
  retina = false,
}: StaticMapOptions): string => {
  const size = staticMapSize(width, height);
  const center = `${lon.toFixed(6)},${lat.toFixed(6)},${zoom}`;
  return (
    `https://api.mapbox.com/styles/v1/${style}/static/${center}/${size.width}x${size.height}` +
    `${retina ? "@2x" : ""}?attribution=false&logo=false&access_token=${encodeURIComponent(MAPBOX_TOKEN)}`
  );
};

/** Ground metres per CSS pixel of a static map (512px tiles) at this latitude and zoom. */
export const metresPerPixel = (lat: number, zoom: number) =>
  (78271.517 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);

const SCALE_STEPS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000];

/**
 * The longest round distance whose bar fits in `maxPx`, for a map drawn at `mPerPx`
 * (metres per CSS pixel as displayed — divide by any cover upscale first).
 */
export const scaleBar = (mPerPx: number, maxPx: number): { label: string; px: number } | null => {
  if (!Number.isFinite(mPerPx) || mPerPx <= 0) {
    return null;
  }
  const metres = [...SCALE_STEPS].reverse().find((m) => m / mPerPx <= maxPx);
  if (!metres) {
    return null;
  }
  return {
    label: metres >= 1000 ? `${metres / 1000} km` : `${metres} m`,
    px: Math.round(metres / mPerPx),
  };
};
